/**
 * Natural Language Excel Operations Page
 *
 * Allows users to issue commands in natural language to perform Excel operations.
 * Examples:
 *   - "Nettoie cette sélection"
 *   - "Crée un graphique des ventes par mois"
 *   - "Trouve les doublons dans la colonne Client"
 *   - "Détecte les anomalies dans la colonne Montant"
 */

import React, { useState } from 'react';
import {
  Text,
  Button,
  Input,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { SendRegular } from '@fluentui/react-icons';
import { OperationPlanCard } from '../components/features/OperationPlanCard';
import { OperationPreviewPanel } from '../components/features/OperationPreviewPanel';
import type { ExcelOperationPlan } from '@excel-ai-studio/shared';

export const NaturalLanguagePage: React.FC = () => {
  const [command, setCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<ExcelOperationPlan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyzeCommand = async () => {
    if (!command.trim()) {
      setError('Veuillez entrer une commande');
      return;
    }

    setLoading(true);
    setError(null);
    setPlan(null);

    try {
      const response = await fetch('/api/natural-language-operation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: command.trim() }),
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur: ${response.statusText}`);
      }

      const data = await response.json();
      setPlan(data);
    } catch (err) {
      setError((err as Error).message || 'Erreur lors de l\'analyse de la commande');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!plan) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/execute-excel-operation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan),
      });

      if (!response.ok) {
        throw new Error(`Erreur lors de l'exécution: ${response.statusText}`);
      }

      setCommand('');
      setPlan(null);
      // Show success message
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Erreur lors de l\'exécution');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPlan(null);
    setError(null);
  };

  return (
    <div>
      <div className="card">
        <Text className="section-title">Opérations Excel en Langage Naturel</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
          Décrivez l'opération que vous souhaitez effectuer en langage naturel.
        </Text>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input
            placeholder="Ex: Nettoie cette sélection, Crée un graphique des ventes par mois..."
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleAnalyzeCommand();
              }
            }}
            disabled={loading}
            style={{ flex: 1 }}
          />
          <Button
            icon={<SendRegular />}
            onClick={handleAnalyzeCommand}
            disabled={loading || !command.trim()}
            appearance="primary"
          >
            Analyser
          </Button>
        </div>

        {error && (
          <MessageBar intent="error" style={{ borderRadius: 4, marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>Erreur</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        )}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Spinner size="small" />
            <Text size={200}>Analyse en cours...</Text>
          </div>
        )}
      </div>

      {plan && (
        <>
          <OperationPlanCard plan={plan} />
          <OperationPreviewPanel
            plan={plan}
            onExecute={handleExecute}
            onCancel={handleCancel}
            loading={loading}
          />
        </>
      )}

      <div className="card">
        <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 8 }}>
          Exemples de commandes
        </Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ExampleCommand text="Nettoie cette sélection" />
          <ExampleCommand text="Uniformise les emails de la colonne B" />
          <ExampleCommand text="Crée un tableau croisé par département" />
          <ExampleCommand text="Trouve les doublons dans la colonne Client" />
          <ExampleCommand text="Surligne les anomalies dans la colonne Montant" />
          <ExampleCommand text="Crée un graphique des ventes par mois" />
          <ExampleCommand text="Filtre les lignes où le montant est supérieur à 5000" />
          <ExampleCommand text="Détecte les anomalies dans les salaires" />
        </div>
      </div>
    </div>
  );
};

const ExampleCommand: React.FC<{ text: string }> = ({ text }) => (
  <Text size={200} style={{ fontFamily: 'monospace', color: 'var(--colorBrandForeground1)' }}>
    • {text}
  </Text>
);
